import { useCallback, useRef, useEffect, useState } from 'react';

interface SmoothScrollOptions {
  duration?: number;
  offset?: number;
  easing?: (t: number) => number;
}

interface NavigationSection {
  id: string;
  label: string;
  offset?: number;
}

/**
 * Hook para navegação suave entre seções
 */
export const useSmoothScroll = (options: SmoothScrollOptions = {}) => {
  const {
    duration = 800,
    offset = -80, // Para compensar header fixo
    easing = (t: number) => t * (2 - t) // ease-out quadratic
  } = options;

  const scrollToElement = useCallback((
    target: string | HTMLElement,
    customOptions?: Partial<SmoothScrollOptions>
  ) => {
    const element = typeof target === 'string' 
      ? document.querySelector(target) as HTMLElement
      : target;

    if (!element) {
      console.warn(`Element not found: ${target}`);
      return;
    }

    const startPosition = window.scrollY;
    const targetPosition = element.offsetTop + (customOptions?.offset || offset);
    const distance = targetPosition - startPosition;
    const scrollDuration = customOptions?.duration || duration;
    const scrollEasing = customOptions?.easing || easing;

    let startTime: number | null = null;

    const animateScroll = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / scrollDuration, 1);
      const easedProgress = scrollEasing(progress);
      
      const currentPosition = startPosition + (distance * easedProgress);
      window.scrollTo(0, currentPosition);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }, [duration, offset, easing]);

  return { scrollToElement };
};

/**
 * Hook para gerenciar navegação entre seções da página
 */
export const usePageNavigation = (sections: NavigationSection[]) => {
  const { scrollToElement } = useSmoothScroll();
  const activeSection = useRef<string>('');

  const navigateToSection = useCallback((sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      scrollToElement(`#${sectionId}`, { offset: section.offset });
      activeSection.current = sectionId;
    }
  }, [sections, scrollToElement]);

  // Detectar seção ativa baseada no scroll
  useEffect(() => {
    const updateActiveSection = () => {
      const scrollPosition = window.scrollY + 100; // offset para melhor UX
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = document.getElementById(section.id);
        
        if (element && element.offsetTop <= scrollPosition) {
          if (activeSection.current !== section.id) {
            activeSection.current = section.id;
            
            // Dispatch custom event para outros componentes
            window.dispatchEvent(new CustomEvent('activeSectionChange', {
              detail: { activeSection: section.id }
            }));
          }
          break;
        }
      }
    };

    let timeoutId: NodeJS.Timeout;
    const throttledUpdate = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        updateActiveSection();
        timeoutId = null;
      }, 100);
    };

    window.addEventListener('scroll', throttledUpdate, { passive: true });
    updateActiveSection(); // Chamada inicial

    return () => {
      window.removeEventListener('scroll', throttledUpdate);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sections]);

  return {
    navigateToSection,
    activeSection: activeSection.current
  };
};

/**
 * Hook para botão "Back to Top"
 */
export const useBackToTop = (threshold: number = 500) => {
  const [showButton, setShowButton] = useState(false);
  const { scrollToElement } = useSmoothScroll();

  useEffect(() => {
    const toggleButton = () => {
      setShowButton(window.scrollY > threshold);
    };

    let timeoutId: NodeJS.Timeout;
    const throttledToggle = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        toggleButton();
        timeoutId = null;
      }, 100);
    };

    window.addEventListener('scroll', throttledToggle, { passive: true });
    toggleButton(); // Chamada inicial

    return () => {
      window.removeEventListener('scroll', throttledToggle);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [threshold]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  return {
    showButton,
    scrollToTop
  };
};